import React, { useRef } from 'react';
import { styled } from '@compiled/react';
import { isIncreasedFlexibilityEnabled } from '@helpcenter/feature-flags';
import { di } from 'react-magnetic-di';
import { useFragment } from 'react-relay';
import { graphql } from 'relay-runtime';
import Grid from '@atlaskit/grid';
import { Box, xcss } from '@atlaskit/primitives';
import { backgroundStyleFromVisualConfig } from '@atlassian/help-center-common-util/bg-style-from-visual-config';
import { useApplySectionBackground } from '@atlassian/help-center-common-util/use-apply-section-background';
import { useCachedUrl } from '@atlassian/help-center-common-util/use-cached-url';
import { SubsectionViewRenderer } from '../subsection-view-renderer';
import { useSectionAndElementId } from '../use-section-element-id';
import type { sectionViewRendererFragment$key } from './__generated__/sectionViewRendererFragment.graphql';

export const SECTION_IDENTIFIER = 'layout-section';

interface SectionProps {
    section: sectionViewRendererFragment$key;
    hasTopBarBeenSplit?: boolean;
}

const SectionViewRenderer: React.FC<SectionProps> = ({ section, hasTopBarBeenSplit }) => {
    di(useApplySectionBackground, isIncreasedFlexibilityEnabled, useCachedUrl);
    const sectionData = useFragment(
        graphql`
            fragment sectionViewRendererFragment on HelpLayoutSection {
                __id
                subsections {
                    __id
                    ...subsectionViewRendererFragment
                    elements {
                        __id
                        __typename
                    }
                }
                visualConfig {
                    backgroundColor
                    # backgroundType is used in backgroundStyleFromVisualConfig
                    # eslint-disable-next-line relay/unused-fields
                    backgroundType
                    backgroundImage {
                        url
                    }
                    hidden
                }
            }
        `,
        section
    );

    const sectionRef = useRef<HTMLElement>(null);

    const subsections = sectionData?.subsections;
    let backgroundColor = sectionData?.visualConfig?.backgroundColor;
    let backgroundImageUrl = sectionData?.visualConfig?.backgroundImage?.url;
    const isIncreasedFlexibilityEnabledWithTopBarSplit = isIncreasedFlexibilityEnabled() && hasTopBarBeenSplit;

    if (isIncreasedFlexibilityEnabledWithTopBarSplit) {
        const visualConfigStyle = backgroundStyleFromVisualConfig(sectionData?.visualConfig);
        backgroundColor = visualConfigStyle.backgroundColor;
        backgroundImageUrl = visualConfigStyle.backgroundImage;
    }

    const { cachedBannerUrl } = useCachedUrl(backgroundImageUrl);

    const sectionId = sectionData.__id;
    const element = sectionData?.subsections?.[0]?.elements?.[0];
    const canApplySectionBackground = useApplySectionBackground({ sectionTypename: element?.__typename || '' });

    useSectionAndElementId(element?.__typename || '', sectionId, element?.__id || '');
    const isSectionHidden = Boolean(sectionData?.visualConfig?.hidden);

    const sectionCustomProp = React.useMemo(() => {
        return {
            style: canApplySectionBackground
                ? {
                      backgroundImage: cachedBannerUrl && !isSectionHidden ? `url(${cachedBannerUrl})` : 'inherit',
                      backgroundColor:
                          isIncreasedFlexibilityEnabledWithTopBarSplit && backgroundColor ? backgroundColor : 'initial',
                  }
                : {},
        };
    }, [
        backgroundColor,
        cachedBannerUrl,
        canApplySectionBackground,
        isIncreasedFlexibilityEnabledWithTopBarSplit,
        isSectionHidden,
    ]);

    if (!sectionData || !sectionId) {
        return null;
    }
    if (!subsections || isSectionHidden) {
        return null;
    }
    return (
        <BoxSectionWrapper
            backgroundColor={
                !isIncreasedFlexibilityEnabledWithTopBarSplit && canApplySectionBackground ? backgroundColor : undefined
            }
            isHidden={isSectionHidden}
            data-testid="layout-section-box-wrapper"
        >
            <Box
                id={sectionId}
                ref={sectionRef}
                as={'section'}
                testId={SECTION_IDENTIFIER}
                xcss={styledSection}
                {...sectionCustomProp}
            >
                <Grid testId="layout-section-grid" hasInlinePadding={!isIncreasedFlexibilityEnabled()}>
                    {subsections.map((subsection) => {
                        if (!subsection) {
                            return null;
                        }

                        return <SubsectionViewRenderer key={subsection.__id} subsection={subsection} />;
                    })}
                </Grid>
            </Box>
        </BoxSectionWrapper>
    );
};

export default SectionViewRenderer;

const styledSection = xcss({
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
});

interface BoxSectionWrapperProps {
    backgroundColor?: string | null;
    isHidden?: boolean;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const BoxSectionWrapper = styled.div<BoxSectionWrapperProps>({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Disabled to rollout go/ui-styling-standard tooling, please resolve
    backgroundColor: (props) => (props.backgroundColor && !props.isHidden ? props.backgroundColor : 'none'),
});
